import React, { useState } from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  FormControl,
  FormLabel,
  Input,
  VStack,
  useToast,
  Select,
  Textarea,
  CheckboxGroup,
  Checkbox,
  Stack,
  Text,
  HStack,
} from "@chakra-ui/react";
import axios from "axios";

const CreateUserModal = ({ isOpen, onClose, onSuccess, categories }) => {
  const toast = useToast();
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    password: "",
    role: "customer",
    companyName: "",
    phone: "",
    businessRegistrationNumber: "",
    location: "",
    description: "",
    categories: [],
    bypassEmailVerification: false,
  });
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleRoleChange = (e) => {
    const newRole = e.target.value;
    setFormData((prev) => ({
      ...prev,
      role: newRole,
      // Reset vendor-specific fields if not vendor
      ...(newRole !== "vendor" && {
        companyName: "",
        phone: "",
        businessRegistrationNumber: "",
        location: "",
        description: "",
        categories: [],
      }),
    }));
  };

  const handleCategoriesChange = (selectedCategories) => {
    setFormData((prev) => ({
      ...prev,
      categories: selectedCategories,
    }));
  };

  const handleCheckboxChange = (e) => {
    setFormData((prev) => ({
      ...prev,
      bypassEmailVerification: e.target.checked,
    }));
  };

  const handleSubmit = async () => {
    if (!formData.name || !formData.email || !formData.password) {
      toast({
        title: "Missing Fields",
        description: "Please fill in name, email, and password.",
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    try {
      setLoading(true);
      const token = localStorage.getItem("authToken");
      const config = {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      };

      const userData = {
        name: formData.name,
        email: formData.email,
        password: formData.password,
        role: formData.role,
        bypassEmailVerification: formData.bypassEmailVerification,
      };

      // Add vendor-specific fields if role is vendor
      if (formData.role === "vendor") {
        userData.companyName = formData.companyName;
        userData.phone = formData.phone;
        userData.businessRegistrationNumber =
          formData.businessRegistrationNumber;
        userData.location = formData.location;
        userData.description = formData.description;
        userData.categories = formData.categories;
      }

      await axios.post("/api/user/admin/create", userData, config);

      toast({
        title: "User Created",
        description: `${
          formData.role.charAt(0).toUpperCase() + formData.role.slice(1)
        } created successfully.`,
        status: "success",
        duration: 3000,
        isClosable: true,
      });

      // Reset form
      setFormData({
        name: "",
        email: "",
        password: "",
        role: "customer",
        companyName: "",
        phone: "",
        businessRegistrationNumber: "",
        location: "",
        description: "",
        categories: [],
        bypassEmailVerification: false,
      });

      setLoading(false);
      onSuccess();
      onClose();
    } catch (error) {
      toast({
        title: "Error Creating User",
        description: error.response?.data?.message || "Failed to create user",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
      setLoading(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Create New User</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack spacing={4}>
            <FormControl isRequired>
              <FormLabel>Role</FormLabel>
              <Select
                name="role"
                value={formData.role}
                onChange={handleRoleChange}
              >
                <option value="customer">Customer</option>
                <option value="vendor">Vendor</option>
                <option value="admin">Admin</option>
              </Select>
            </FormControl>

            <FormControl isRequired>
              <FormLabel>Name</FormLabel>
              <Input
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder="Enter full name"
              />
            </FormControl>

            <FormControl isRequired>
              <FormLabel>Email</FormLabel>
              <Input
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="Enter email address"
              />
            </FormControl>

            <FormControl isRequired>
              <FormLabel>Password</FormLabel>
              <Input
                name="password"
                type="password"
                value={formData.password}
                onChange={handleChange}
                placeholder="Enter password (min 6 characters)"
              />
            </FormControl>

            <FormControl>
              <HStack>
                <Checkbox
                  isChecked={formData.bypassEmailVerification}
                  onChange={handleCheckboxChange}
                >
                  Bypass Email Verification
                </Checkbox>
              </HStack>
              <Text fontSize="sm" color="gray.600" mt={1}>
                If checked, user can login immediately without verifying email
              </Text>
            </FormControl>

            {formData.role === "vendor" && (
              <>
                <FormControl>
                  <FormLabel>Company Name</FormLabel>
                  <Input
                    name="companyName"
                    value={formData.companyName}
                    onChange={handleChange}
                    placeholder="Enter company name"
                  />
                </FormControl>

                <FormControl>
                  <FormLabel>Phone</FormLabel>
                  <Input
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="Enter phone number"
                  />
                </FormControl>

                <FormControl>
                  <FormLabel>Business Registration Number</FormLabel>
                  <Input
                    name="businessRegistrationNumber"
                    value={formData.businessRegistrationNumber}
                    onChange={handleChange}
                    placeholder="Enter registration number"
                  />
                </FormControl>

                <FormControl>
                  <FormLabel>Location</FormLabel>
                  <Input
                    name="location"
                    value={formData.location}
                    onChange={handleChange}
                    placeholder="Enter location"
                  />
                </FormControl>

                <FormControl>
                  <FormLabel>Description</FormLabel>
                  <Textarea
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    placeholder="Enter business description"
                  />
                </FormControl>

                <FormControl>
                  <FormLabel>Categories</FormLabel>
                  <CheckboxGroup
                    value={formData.categories}
                    onChange={handleCategoriesChange}
                  >
                    <Stack spacing={2} maxH="200px" overflowY="auto">
                      {categories.map((cat) => (
                        <Checkbox key={cat.name} value={cat.name}>
                          {cat.name}
                        </Checkbox>
                      ))}
                    </Stack>
                  </CheckboxGroup>
                </FormControl>
              </>
            )}
          </VStack>
        </ModalBody>

        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={onClose}>
            Cancel
          </Button>
          <Button
            colorScheme="green"
            onClick={handleSubmit}
            isLoading={loading}
          >
            Create User
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default CreateUserModal;
